import type { BoundedText, PodArrayLimitReason, PodArraySummary, PodContainerSpec, PodResourceQuantity, PodStructure, Section } from "@k8s-dashboard/contracts";
import { Panel } from "@/components/primitives";
import { useT, type Translate } from "@/i18n/I18nProvider";

const LIMIT_MESSAGE: Record<PodArrayLimitReason, string> = {
  item_limit: "pod.limit.item_limit", byte_limit: "pod.limit.byte_limit",
};
const KIND_MESSAGE: Record<PodContainerSpec["kind"], string> = {
  app: "pod.kind.app", init: "pod.kind.init", ephemeral: "pod.kind.ephemeral",
};

function textValue(value: BoundedText, t: Translate): string {
  if (value.state === "absent") return t("pod.absent");
  if (value.state === "withheld_too_large") return t("pod.withheld");
  return `${value.value ?? ""}${value.truncated ? ` ${t("pod.truncated")}` : ""}`;
}

function count(summary: PodArraySummary, t: Translate): string {
  const base = t("pod.count", { shown: summary.shown, total: summary.total });
  return summary.truncated ? `${base} · ${t(LIMIT_MESSAGE[summary.reason ?? "item_limit"])}` : base;
}

function quantities(values: PodResourceQuantity, t: Translate): string {
  return [
    `CPU ${textValue(values.cpu, t)}`,
    `Memory ${textValue(values.memory, t)}`,
    `ephemeral-storage ${textValue(values.ephemeralStorage, t)}`,
  ].join(" · ");
}

export function PodStructureCard({ section, referenceIso }: { section?: Section<PodStructure>; referenceIso?: string }) {
  const { t } = useT("pod");
  if (!section) return null;
  if (section.status === "degraded" && !section.data) {
    const key = section.reason === "structured_detail_too_large" ? "pod.tooLarge" :
      section.reason === "structured_detail_unsupported" ? "pod.unsupported" : "pod.unavailable";
    return (
      <Panel title={t("pod.title")} section={section} referenceIso={referenceIso}>
        <div className="state state--degraded" role="note" data-state={section.reason}>
          <span className="state__glyph" aria-hidden="true">▲</span>
          <span className="state__title">{t(key)}</span>
        </div>
      </Panel>
    );
  }
  if (!section.data) return null;
  const data = section.data;
  return (
    <Panel title={t("pod.title")} section={section} referenceIso={referenceIso}>
      <div className="grid">
        <section>
          <h3>{t("pod.facts")}</h3>
          <dl className="resource-drawer__facts">
            <div><dt>{t("pod.serviceAccount")}</dt><dd>{textValue(data.serviceAccountName, t)}</dd></div>
            <div><dt>{t("pod.qos")}</dt><dd>{data.qosClass}</dd></div>
            <div><dt>{t("pod.priority")}</dt><dd>{textValue(data.priorityClassName, t)}</dd></div>
            <div><dt>{t("pod.node")}</dt><dd>{textValue(data.nodeName, t)}</dd></div>
            <div><dt>{t("pod.ips")}</dt><dd>{data.podIPs.items.map((ip) => textValue(ip, t)).join(", ") || t("pod.absent")} · {count(data.podIPs, t)}</dd></div>
            <div><dt>{t("pod.affinity")}</dt><dd>{[
              data.affinity.nodeAffinity && "node", data.affinity.podAffinity && "pod", data.affinity.podAntiAffinity && "podAnti",
            ].filter(Boolean).join(", ") || t("pod.absent")}</dd></div>
            <div><dt>{t("pod.spread")}</dt><dd>{data.topologySpreadCount}</dd></div>
          </dl>
        </section>

        <section>
          <h3>{t("pod.containers")}</h3>
          <p className="muted">{t("pod.categories", {
            app: count(data.containers.app, t), init: count(data.containers.init, t), ephemeral: count(data.containers.ephemeral, t),
          })}</p>
          <div className="panel__scroll" tabIndex={0} role="group" aria-label={t("pod.containers")}>
            <table className="ds-data-table ds-data-table--compact">
              <thead><tr><th scope="col">{t("pod.kind")}</th><th scope="col">{t("pod.name")}</th><th scope="col">{t("pod.ports")}</th><th scope="col">{t("pod.mounts")}</th><th scope="col">{t("pod.requests")}</th><th scope="col">{t("pod.limits")}</th></tr></thead>
              <tbody>{data.containers.items.map((row, index) => (
                <tr key={`${row.kind}-${row.name.value ?? index}`}>
                  <td>{t(KIND_MESSAGE[row.kind])}</td>
                  <td className="ds-ident">{textValue(row.name, t)}</td>
                  <td>{row.ports.items.map((port) => `${textValue(port.name, t)} ${port.containerPort}/${port.protocol}`).join(", ") || t("pod.absent")}{row.ports.truncated ? ` · ${count(row.ports, t)}` : ""}</td>
                  <td>{row.volumeMounts.items.map((mount) => `${textValue(mount.name, t)} → ${textValue(mount.mountPath, t)}${mount.readOnly ? " (ro)" : ""}${mount.subPathPresent ? ` · ${t("pod.subPath")}` : ""}`).join(", ") || t("pod.absent")}{row.volumeMounts.truncated ? ` · ${count(row.volumeMounts, t)}` : ""}</td>
                  <td>{quantities(row.resources.requests, t)}</td>
                  <td>{quantities(row.resources.limits, t)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <p className="muted">{count(data.containers.summary, t)}</p>
        </section>

        <section>
          <h3>{t("pod.volumes")}</h3>
          <table className="ds-data-table ds-data-table--compact">
            <thead><tr><th scope="col">{t("pod.name")}</th><th scope="col">{t("pod.type")}</th><th scope="col">{t("pod.reference")}</th></tr></thead>
            <tbody>{data.volumes.items.map((volume, index) => (
              <tr key={volume.name.value ?? index}>
                <td className="ds-ident">{textValue(volume.name, t)}</td><td>{volume.type}</td>
                <td>{volume.referenceName ? textValue(volume.referenceName, t) : volume.hostPathPresent ? t("pod.hostPathPresent") : t("pod.absent")}</td>
              </tr>
            ))}</tbody>
          </table>
          <p className="muted">{count(data.volumes, t)}</p>
        </section>

        <section>
          <h3>{t("pod.tolerations")}</h3>
          {data.tolerations.items.length ? (
            <ul>{data.tolerations.items.map((item, index) => <li key={`${item.key.value ?? "withheld"}-${index}`}>{textValue(item.key, t)} · {item.operator} · {item.effect}{item.tolerationSeconds !== undefined ? ` · ${item.tolerationSeconds}s` : ""}</li>)}</ul>
          ) : <p className="muted">{t("pod.absent")}</p>}
          <p className="muted">{count(data.tolerations, t)}</p>
        </section>
      </div>
    </Panel>
  );
}
